import { toast } from "@eisox/design-system";
import { MutationCache } from "@tanstack/react-query";

import { m } from "../paraglide/messages";
import { createMutationFeedbackComponent } from "./components/mutation-feedback.component";
import { describeError, toastIdFor, toastMessage } from "./mutation-feedback.helper";
import { createErrorDialogStore } from "./mutation-feedback.store";
import type { MutationFeedbackOptions } from "./mutation-feedback.type";

/** Below this, the mutation settles before the eye registers the spinner. */
const DEFAULT_LOADING_DELAY_MS = 300;
/** An error toast carries an action, so it has to outlive Base UI's 5 s default. */
const DEFAULT_ERROR_DURATION_MS = 10_000;

const createMutationFeedback = ({
  app,
  loadingDelay = DEFAULT_LOADING_DELAY_MS,
  errorDuration = DEFAULT_ERROR_DURATION_MS,
}: MutationFeedbackOptions) => {
  const store = createErrorDialogStore();
  const scheduled = new Map<number, ReturnType<typeof setTimeout>>();
  const dismissed = new Set<number>();

  /** Returns whether the loading toast was still pending, meaning it was never painted. */
  const cancelScheduled = (mutationId: number): boolean => {
    const timer = scheduled.get(mutationId);
    if (timer === undefined) return false;
    clearTimeout(timer);
    scheduled.delete(mutationId);
    return true;
  };

  // Every callback stays synchronous: TanStack awaits them before the call site's own.
  const mutationCache = new MutationCache({
    onMutate(_variables, mutation) {
      const toastMeta = mutation.meta?.toast;
      if (toastMeta === false) return;

      const { mutationId } = mutation;
      const message = toastMessage(toastMeta, "loading");

      scheduled.set(
        mutationId,
        setTimeout(() => {
          scheduled.delete(mutationId);
          toast.loading(message, { id: toastIdFor(mutationId), onClose: () => dismissed.add(mutationId) });
        }, loadingDelay),
      );
    },

    onSuccess(_data, _variables, _onMutateResult, mutation) {
      const { mutationId } = mutation;
      const toastMeta = mutation.meta?.toast;
      cancelScheduled(mutationId);

      // A spinner closed by hand is not resurrected just to say it worked.
      if (toastMeta === false || dismissed.has(mutationId)) return;

      toast.success(toastMessage(toastMeta, "success"), { id: toastIdFor(mutationId) });
    },

    onError(rejection, _variables, _onMutateResult, mutation) {
      const { mutationId } = mutation;
      const toastMeta = mutation.meta?.toast;
      const spinnerOnScreen = !cancelScheduled(mutationId) && !dismissed.has(mutationId);
      if (toastMeta === false) return;

      const error = describeError(rejection);
      if (!error) {
        if (spinnerOnScreen) toast.dismiss(toastIdFor(mutationId));
        return;
      }

      // A spinner closed by hand must not bury the failure: reopen under a fresh id.
      const toastId = toastIdFor(mutationId, dismissed.has(mutationId) ? "-retry" : "");

      toast.error(toastMessage(toastMeta, "error"), {
        id: toastId,
        duration: errorDuration,
        action: {
          label: m.details(),
          onClick: () => {
            store.open(error);
            toast.dismiss(toastId);
          },
        },
      });
    },

    onSettled(_data, _error, _variables, _onMutateResult, mutation) {
      cancelScheduled(mutation.mutationId);
      dismissed.delete(mutation.mutationId);
    },
  });

  return { mutationCache, MutationFeedback: createMutationFeedbackComponent({ app, store }) };
};

export { createMutationFeedback };
